import { FC, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, DialogOpenChangeDetails, Stack } from '@chakra-ui/react';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { useCreateApplicationNote } from '~/core/shared/api/middle-office/application';
import { Dialog, FormField, Input, Wysiwyg } from '~/core/shared/components';
import UnsavedModal from '~/core/shared/components/UnsavedModal.component';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { NoteCreateFormValues } from '~/core/shared/types';
import { MessagesTabContext } from '../application/tabs';

type NoteCreateModalProps = {
  documentId: string;
  documentDefinition: string;
};

const validationSchema: yup.ObjectSchema<NoteCreateFormValues> = yup.object({
  content: yup.string().trim().required(),
  subject: yup.string().trim().required(),
});

export const NoteCreateModal: FC<NoteCreateModalProps> = ({
  documentId,
  documentDefinition,
}) => {
  const { noteCreateModalApi } = useContext(MessagesTabContext);
  const unsavedChangesModalApi = useOpenClose();

  const defaultValues: NoteCreateFormValues = {
    content: '',
    subject: '',
  };

  // TODO: @haxxxton refactor to use form blocks
  const resolver = useFormResolver<NoteCreateFormValues>(validationSchema);
  const form = useForm<NoteCreateFormValues>({
    defaultValues,
    resolver,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const { createNote } = useCreateApplicationNote(
    documentId,
    documentDefinition,
  );

  const onSubmit = useEventHandler(
    async (formValues: NoteCreateFormValues): Promise<void> => {
      await createNote(formValues);
      form.reset(defaultValues);
      noteCreateModalApi.onClose();
    },
  );

  const handleCloseAndReset = useEventHandler(() => {
    form.reset();
    unsavedChangesModalApi.onClose();
    noteCreateModalApi.onClose();
  });

  const handleClose = useEventHandler(() => {
    if (form.formState.isDirty) {
      unsavedChangesModalApi.onOpen();
    } else {
      handleCloseAndReset();
    }
  });

  // TODO: move to onClose prop of useOpenClose
  const onOpenChange = useEventHandler(({ open }: DialogOpenChangeDetails) => {
    if (open) return;
    handleClose();
  });

  return (
    <Dialog
      header="Add private note"
      onOpenChange={onOpenChange}
      open={noteCreateModalApi.open}
      size="lg"
      subHeader="Store internal updates to an application"
    >
      <FormProvider {...form}>
        <Stack
          as="form"
          className="privateNote privateNoteMessage"
          gap={4}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            component={Input}
            label="Subject"
            name="subject"
            required
            variant="outline"
          />

          <FormField
            borderColor="gray.300"
            component={Wysiwyg}
            label="Message text"
            name="content"
            required
            size="lg"
            variant="outline"
          />

          <Stack direction="row" justifyContent="end">
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={handleClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              fontSize="md"
              fontWeight="bold"
              type="submit"
              variant="solid"
            >
              Save private note
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
      <UnsavedModal
        close={handleCloseAndReset}
        modalApi={unsavedChangesModalApi}
      />
    </Dialog>
  );
};
